import React, { useMemo, useRef, useState } from 'react';
import filesize from 'filesize';
import {
  assetOriginalUrl,
  MagicRef,
  MAX_REFS,
  refAssetId,
  refExtension,
  refImageUrl,
  refName,
} from '../model';
import { TM } from '../tokens';
import { CloseIcon, GripIcon, PlusIcon } from '../icons';
import { useImageSize, useRefBytes } from '../useImageMeta';
import { AssetThumb } from './AssetThumb';
import { LightboxImage, MagicImageLightbox } from './MagicImageLightbox';
import { HintIcon } from './ui';

// Custom drag type so the reference reorder DnD is distinguishable from a
// desktop file-drop (which the whole panel accepts) — file drags carry "Files",
// reorder drags carry this token, letting each handler ignore the other.
const REF_DND_TYPE = 'application/x-magic-ref';

// Pixel size and file weight of one reference, under its name. Both are resolved
// asynchronously (dimensions from the loaded image, an upload's weight from its
// own blob), so each part appears as it becomes known and the line is omitted
// entirely while neither is. A component of its own because the hooks it needs
// can't be called from inside the rows' .map().
const RefMetrics: React.FC<{ item: MagicRef }> = ({ item }) => {
  const size = useImageSize(refImageUrl(item));
  const bytes = useRefBytes(item);
  const parts = [size ? `${size.w} × ${size.h}` : null, bytes !== null ? filesize(bytes) : null];
  const text = parts.filter(Boolean).join(' · ');
  if (!text) return null;
  // Dimensions are read from the image the studio actually shows (and sends). For
  // an asset whose original can't be fed to the model — a PSD, or one over the
  // size cap — that's the flattened preview render, while the weight still
  // describes the original file; the tooltip says so rather than implying both
  // numbers describe the same bytes.
  const previewOnly = item.kind === 'asset' && !assetOriginalUrl(item.asset);
  return (
    <div
      title={previewOnly ? 'Preview render size · original file weight' : 'Image size · weight'}
      style={{
        fontSize: 10,
        color: TM.textMute,
        marginTop: 2,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {text}
    </div>
  );
};

// Left panel: the reference images passed (few-shot) to the model. The first
// ref is the primary "style anchor". References can come from Asset Lab, the
// user's files, the clipboard, or be dropped in from the desktop. Refs can be
// reordered by drag (first = primary) and clicked to preview full size.
export const RefsPanel: React.FC<{
  refs: MagicRef[];
  primaryAssetId: number | null;
  onRemove: (key: string) => void;
  onReorder: (from: number, to: number) => void;
  onOpenPicker: () => void;
  onAddFiles: (files: FileList | File[]) => void;
}> = ({ refs, primaryAssetId, onRemove, onReorder, onOpenPicker, onAddFiles }) => {
  const [dragOver, setDragOver] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  // Index into `zoomImages` of the reference shown full size, or null.
  const [zoom, setZoom] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const full = refs.length >= MAX_REFS;

  // The lightbox set: every reference that has an image, in panel order, so the
  // arrows step through the session's refs instead of showing one in isolation.
  const zoomImages = useMemo(
    () =>
      refs
        .map((ref, idx): LightboxImage | null => {
          const src = refImageUrl(ref);
          return src
            ? {
                key: ref.key,
                src,
                name: refName(ref),
                role: idx === 0 ? 'Primary reference' : `Reference ${idx + 1}`,
              }
            : null;
        })
        .filter((img): img is LightboxImage => img !== null),
    [refs],
  );

  const isRefDrag = (e: React.DragEvent) => e.dataTransfer.types.includes(REF_DND_TYPE);

  const onDrop = (e: React.DragEvent) => {
    // Reorder drops are handled per-row; only react to real file drops here.
    if (isRefDrag(e)) return;
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) onAddFiles(e.dataTransfer.files);
  };

  const endReorder = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <div
      className={`al-magic-refs${dragOver ? ' al-magic-refs_dragover' : ''}`}
      onDragOver={(e) => {
        if (isRefDrag(e)) return; // internal reorder — handled by the rows
        e.preventDefault();
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={(e) => {
        // Ignore drag-leave bubbling from children.
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setDragOver(false);
      }}
      onDrop={onDrop}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: TM.text }}>References</div>
        <HintIcon
          text={
            <>
              Up to {MAX_REFS} images. Drag to reorder — the first ref drives subject &amp; style
              most. Refs are passed inline (few-shot) to the model with the prompt.
            </>
          }
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
        {refs.map((ref, idx) => (
          <div
            key={ref.key}
            onDragOver={(e) => {
              if (!isRefDrag(e)) return;
              e.preventDefault();
              e.stopPropagation();
              if (overIndex !== idx) setOverIndex(idx);
            }}
            onDrop={(e) => {
              if (!isRefDrag(e)) return;
              e.preventDefault();
              e.stopPropagation();
              if (dragIndex !== null) onReorder(dragIndex, idx);
              endReorder();
            }}
            style={{
              display: 'flex',
              gap: 8,
              padding: 8,
              borderRadius: 6,
              border: `1px solid ${idx === 0 ? TM.blue : TM.border}`,
              background: idx === 0 ? TM.blueWash : '#fff',
              position: 'relative',
              opacity: dragIndex === idx ? 0.4 : 1,
              outline:
                overIndex === idx && dragIndex !== null && dragIndex !== idx
                  ? `2px dashed ${TM.blue}`
                  : 'none',
              outlineOffset: -2,
            }}
          >
            {/* Drag handle — the reorder source. */}
            <div
              className="al-magic-ref-grip"
              draggable
              title="Drag to reorder · first ref is primary"
              onDragStart={(e) => {
                // Drag the whole row, not just the tiny grip.
                const row = e.currentTarget.parentElement;
                if (row) e.dataTransfer.setDragImage(row, 20, 24);
                e.dataTransfer.setData(REF_DND_TYPE, String(idx));
                e.dataTransfer.effectAllowed = 'move';
                setDragIndex(idx);
              }}
              onDragEnd={endReorder}
            >
              <GripIcon size={12} />
            </div>

            {/* Thumbnail — click to preview full size. */}
            <button
              type="button"
              className="al-magic-ref-thumb-btn"
              title="Click to enlarge"
              onClick={() => {
                const at = zoomImages.findIndex((img) => img.key === ref.key);
                if (at >= 0) setZoom(at);
              }}
            >
              <AssetThumb src={refImageUrl(ref)} name={refName(ref)} size={48} />
            </button>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  color: TM.text,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {refName(ref)}
              </div>
              <RefMetrics item={ref} />
              <div style={{ fontSize: 10, color: TM.textFaint, marginTop: 2 }}>
                {idx === 0 ? 'Primary · style anchor' : 'Supporting ref'}
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    padding: '1px 5px',
                    borderRadius: 3,
                    background: TM.blueFaint,
                    color: TM.blue,
                  }}
                >
                  {refExtension(ref)}
                </span>
                {ref.kind === 'asset' && refAssetId(ref) === primaryAssetId && (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      padding: '1px 5px',
                      borderRadius: 3,
                      background: TM.greenSoft,
                      color: '#0F5A1F',
                    }}
                  >
                    from this asset
                  </span>
                )}
                {ref.kind === 'upload' && (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      padding: '1px 5px',
                      borderRadius: 3,
                      background: TM.bgSoft,
                      color: TM.textMute,
                    }}
                  >
                    uploaded
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              title="Remove reference"
              onClick={() => onRemove(ref.key)}
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 18,
                height: 18,
                borderRadius: 3,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: TM.textFaint,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CloseIcon size={10} />
            </button>
          </div>
        ))}

        {!full && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={onOpenPicker} className="al-magic-ref-add">
              <PlusIcon size={12} /> Asset Lab
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="al-magic-ref-add"
            >
              <PlusIcon size={12} /> Upload
            </button>
          </div>
        )}
      </div>

      <div style={{ fontSize: 11, color: TM.textFaint, lineHeight: 1.4, marginTop: 2 }}>
        {full
          ? `Maximum ${MAX_REFS} references reached.`
          : 'Pick from Asset Lab, upload a file, paste from clipboard, or drop images here.'}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) onAddFiles(e.target.files);
          e.target.value = ''; // allow re-selecting the same file
        }}
      />

      {zoom !== null && (
        <MagicImageLightbox
          images={zoomImages}
          index={zoom}
          onIndexChange={setZoom}
          onClose={() => setZoom(null)}
        />
      )}
    </div>
  );
};
